import { type Account, api } from '@/api/client'
import { cn } from '@/lib/cn'
import { useQuery } from '@tanstack/react-query'
import { Check, ChevronDown, Search, X } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

interface Props {
  value: string
  onChange: (code: string, account: Account | null) => void
  className?: string
  disabled?: boolean
  placeholder?: string
  error?: boolean
}

// AccountCodePicker is a searchable combobox over the chart of accounts.
// Filters to detail (is_detail=true) active accounts only — root accounts
// cannot receive journal lines. Returns both the code string and the
// resolved Account (so callers can display the name without a roundtrip).
export function AccountCodePicker({
  value,
  onChange,
  className,
  disabled,
  placeholder,
  error,
}: Props) {
  const { t } = useTranslation('accounting')
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const wrapperRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  // Focus the search input when the popover opens.
  useEffect(() => {
    if (open) inputRef.current?.focus()
  }, [open])

  const accountsQuery = useQuery({
    queryKey: ['accounting', 'accounts', 'picker'],
    queryFn: () => api.accounting.listAccounts({ status: 'active' }),
    staleTime: 60_000,
  })

  const selected = useMemo(
    () => accountsQuery.data?.find((a) => a.account_code === value) ?? null,
    [accountsQuery.data, value],
  )

  const filtered = useMemo(() => {
    const all = (accountsQuery.data ?? []).filter((a) => a.is_detail)
    const q = query.trim().toLowerCase()
    if (!q) return all.slice(0, 50)
    return all
      .filter(
        (a) => a.account_code.toLowerCase().includes(q) || a.account_name.toLowerCase().includes(q),
      )
      .slice(0, 50)
  }, [accountsQuery.data, query])

  // Close on outside click
  useEffect(() => {
    if (!open) return
    const onClick = (e: MouseEvent) => {
      if (!wrapperRef.current?.contains(e.target as Node)) setOpen(false)
    }
    window.addEventListener('mousedown', onClick)
    return () => window.removeEventListener('mousedown', onClick)
  }, [open])

  // Close on Escape
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open])

  return (
    <div ref={wrapperRef} className={cn('relative', className)}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
        className={cn(
          'flex min-h-[44px] w-full items-center justify-between gap-2 rounded-xl border bg-background px-3 py-2 text-left text-sm transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
          error ? 'border-red-500' : 'border-border',
          disabled && 'cursor-not-allowed opacity-50',
        )}
      >
        {selected ? (
          <span className="flex min-w-0 items-center gap-2">
            <span className="font-mono font-semibold tabular-nums">{selected.account_code}</span>
            <span className="truncate text-muted-foreground">
              {selected.account_name}
            </span>
          </span>
        ) : (
          <span className="text-muted-foreground">{placeholder ?? t('account.pick_placeholder')}</span>
        )}
        <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
      </button>

      {open ? (
        <div className="absolute z-50 mt-1 w-full overflow-hidden rounded-xl border border-border bg-background shadow-lg">
          <div className="flex items-center gap-2 border-b border-border px-3 py-2">
            <Search className="h-4 w-4 text-muted-foreground" />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t('account.pick_placeholder')}
              className="flex-1 bg-transparent text-sm outline-none"
            />
            {query ? (
              <button
                type="button"
                onClick={() => setQuery('')}
                className="text-muted-foreground hover:text-foreground"
              >
                <X className="h-4 w-4" />
              </button>
            ) : null}
          </div>
          <ul className="max-h-64 overflow-y-auto py-1">
            {accountsQuery.isLoading ? (
              <li className="px-3 py-2 text-xs text-muted-foreground">…</li>
            ) : filtered.length === 0 ? (
              <li className="px-3 py-4 text-center text-xs text-muted-foreground">
                {t('account.no_results')}
              </li>
            ) : (
              filtered.map((a) => {
                const isSelected = a.account_code === value
                return (
                  <li key={a.id}>
                    <button
                      type="button"
                      onClick={() => {
                        onChange(a.account_code, a)
                        setOpen(false)
                        setQuery('')
                      }}
                      className={cn(
                        'flex w-full items-center justify-between gap-3 px-3 py-2 text-left text-sm transition hover:bg-muted',
                        isSelected && 'bg-muted',
                      )}
                    >
                      <span className="flex min-w-0 items-center gap-2">
                        <span className="font-mono font-semibold tabular-nums">
                          {a.account_code}
                        </span>
                        <span className="truncate text-muted-foreground">
                          {a.account_name}
                        </span>
                      </span>
                      {isSelected ? (
                        <Check className="h-4 w-4 text-muted-foreground" />
                      ) : (
                        <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
                          {t(`account_type.${a.account_type}`)}
                        </span>
                      )}
                    </button>
                  </li>
                )
              })
            )}
          </ul>
        </div>
      ) : null}
    </div>
  )
}
